import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Trash2, RefreshCw, Shield, FolderKanban, CheckSquare, Users, Target, IndianRupee, Database, Bell, AlertTriangle, Stethoscope
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const SECTIONS = [
  { key: 'projects', label: 'Projects', icon: FolderKanban, endpoint: '/projects', deleteEndpoint: '/admin/projects' },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare, endpoint: '/tasks', deleteEndpoint: '/admin/tasks' },
  { key: 'leads', label: 'Leads', icon: Target, endpoint: '/leads', deleteEndpoint: '/admin/leads' },
  { key: 'users', label: 'Users', icon: Users, endpoint: '/users', deleteEndpoint: '/users' },
  { key: 'accounts', label: 'Payments', icon: IndianRupee, endpoint: null, deleteEndpoint: '/admin/payments' },
];

const AdminPanelPage = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('projects');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAdmin = (user?.roles || [user?.role]).includes('admin');

  // Factory Reset state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const handleFactoryReset = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Type RESET exactly to confirm');
      return;
    }
    setResetBusy(true);
    try {
      const res = await api.post('/admin/factory-reset', { confirm: 'RESET' });
      toast.success(res.data.message || 'Factory reset complete');
      setShowResetDialog(false);
      setResetConfirmText('');
      // Force a hard reload so all in-memory state is dropped
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  useEffect(() => { fetchItems(); }, [activeSection]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const section = SECTIONS.find(s => s.key === activeSection);
      if (!section?.endpoint) { setItems([]); setLoading(false); return; }
      const res = await api.get(section.endpoint);
      setItems(res.data);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    const section = SECTIONS.find(s => s.key === activeSection);
    try {
      await api.delete(`${section.deleteEndpoint}/${deleteTarget.id}`);
      toast.success('Deleted successfully');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleClearNotifications = async () => {
    try {
      await api.delete('/admin/notifications/clear-all');
      toast.success('All notifications cleared');
    } catch (err) {
      toast.error('Failed');
    }
  };

  // Attendance diagnostic + backfill state
  const [showDiagnoseDialog, setShowDiagnoseDialog] = useState(false);
  const [diagnoseReport, setDiagnoseReport] = useState(null);
  const [diagnoseBusy, setDiagnoseBusy] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');

  const runDiagnose = async () => {
    setDiagnoseBusy(true);
    setDiagnoseReport(null);
    setShowDiagnoseDialog(true);
    try {
      const res = await api.get('/admin/attendance/diagnose');
      setDiagnoseReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnose failed');
      setShowDiagnoseDialog(false);
    } finally {
      setDiagnoseBusy(false);
    }
  };

  const runPurge = async () => {
    if (purgeConfirmText !== 'PURGE') {
      toast.error('Type PURGE exactly to confirm');
      return;
    }
    setDiagnoseBusy(true);
    try {
      const res = await api.post('/admin/attendance/backfill', { confirm: 'PURGE' });
      toast.success(res.data.message);
      setPurgeConfirmText('');
      // Re-run diagnose so the counts update
      const res2 = await api.get('/admin/attendance/diagnose');
      setDiagnoseReport(res2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally {
      setDiagnoseBusy(false);
    }
  };

  const getItemLabel = (item) => {
    if (activeSection === 'projects') return { title: item.title, sub: `${item.client_name || ''} | ${item.brand || 'wittyshadows'}` };
    if (activeSection === 'tasks') return { title: item.title, sub: `${item.project_title || ''} | ${item.status} | ${item.assigned_to_name || 'Unassigned'}` };
    if (activeSection === 'leads') return { title: item.client_name, sub: `${item.pipeline_stage || ''} | ${item.brand || ''}` };
    if (activeSection === 'users') return { title: item.name, sub: `${item.email} | ${(item.roles || []).join(', ')}` };
    return { title: item.id, sub: '' };
  };

  if (!isAdmin) {
    return <div className="text-center py-20 text-zinc-500">Admin access required</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-rose-400" />
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight">Admin Panel</h1>
            <p className="text-zinc-500 text-sm">Delete or rectify any record</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runDiagnose} className="border-blue-500/40 bg-blue-500/5 text-blue-400 hover:bg-blue-500/15 text-xs" data-testid="diagnose-attendance-btn">
            <Stethoscope className="h-3 w-3 mr-1" /> Repair Attendance Data
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowResetDialog(true)} className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 text-xs" data-testid="open-factory-reset">
            <AlertTriangle className="h-3 w-3 mr-1" /> Factory Reset
          </Button>
          <Button variant="outline" size="sm" onClick={handleClearNotifications} className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs">
            <Bell className="h-3 w-3 mr-1" /> Clear All Notifications
          </Button>
          <Button variant="outline" size="sm" onClick={fetchItems} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeSection === s.key ? 'text-rose-400 border-rose-400' : 'text-zinc-500 border-transparent hover:text-white'
            }`} data-testid={`admin-tab-${s.key}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-400"></div></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No {activeSection} found</div>
      ) : (
        <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Name</th>
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Details</th>
                <th className="text-right text-xs text-zinc-400 font-medium px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const label = getItemLabel(item);
                const isSelf = activeSection === 'users' && item.id === user?.id;
                return (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{label.title}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-zinc-500">{label.sub}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!isSelf && (
                        <Button size="sm" variant="outline" onClick={() => confirmDelete(item)}
                          className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs" data-testid={`delete-${item.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      )}
                      {isSelf && <span className="text-xs text-zinc-600">(You)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete <span className="text-white font-medium">{deleteTarget && getItemLabel(deleteTarget).title}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-admin-delete">Delete Permanently</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Factory Reset Confirmation */}
      <Dialog open={showResetDialog} onOpenChange={(o) => { if (!resetBusy) { setShowResetDialog(o); if (!o) setResetConfirmText(''); }}}>
        <DialogContent className="bg-[#121212] border-red-500/40 text-white sm:max-w-md" data-testid="factory-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" /> Factory Reset — DANGER
            </DialogTitle>
            <DialogDescription className="text-zinc-400 space-y-2">
              <span className="block">This will <strong className="text-red-400">permanently delete</strong>:</span>
              <span className="block text-xs ml-2">
                • All projects, leads, clients, tasks, payments<br/>
                • All attendance, time entries, notifications<br/>
                • All freelancers, vault data, transfers, uploaded files<br/>
                • All other users (only YOU will remain as admin)
              </span>
              <span className="block mt-3 text-amber-300">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="text-sm text-zinc-300">Type <span className="font-mono text-red-400 bg-black/40 px-1.5 py-0.5 rounded">RESET</span> below to confirm:</label>
            <Input
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="Type RESET to confirm"
              className="bg-black/40 border-red-500/30 font-mono"
              disabled={resetBusy}
              data-testid="factory-reset-confirm-input"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowResetDialog(false)} disabled={resetBusy} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleFactoryReset} disabled={resetBusy || resetConfirmText !== 'RESET'} className="bg-red-600 hover:bg-red-700" data-testid="confirm-factory-reset">
              {resetBusy ? 'Wiping…' : 'Wipe everything'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance Diagnostic dialog */}
      <Dialog open={showDiagnoseDialog} onOpenChange={(v) => { setShowDiagnoseDialog(v); if (!v) { setDiagnoseReport(null); setPurgeConfirmText(''); } }}>
        <DialogContent className="bg-[#121212] border-blue-500/30 text-white max-w-lg" data-testid="diagnose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-400">
              <Stethoscope className="h-5 w-5" /> Attendance Data Repair
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Scans every attendance record for a broken <code className="text-blue-300">user_id</code> (invalid ObjectId or deleted user). These records used to crash the manager view — the endpoint now skips them safely, and you can purge them here for a clean database.
            </DialogDescription>
          </DialogHeader>

          {diagnoseBusy && !diagnoseReport && (
            <div className="py-8 text-center text-sm text-zinc-400">Scanning attendance records…</div>
          )}

          {diagnoseReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-2xl font-semibold text-emerald-400" data-testid="diagnose-healthy">{diagnoseReport.healthy}</p>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Healthy</p>
                </div>
                <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20 text-center">
                  <p className="text-2xl font-semibold text-amber-400" data-testid="diagnose-invalid">{diagnoseReport.invalid_id_count}</p>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Invalid ID</p>
                </div>
                <div className="p-3 rounded bg-rose-500/10 border border-rose-500/20 text-center">
                  <p className="text-2xl font-semibold text-rose-400" data-testid="diagnose-ghost">{diagnoseReport.ghost_user_count}</p>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Deleted User</p>
                </div>
              </div>

              <div className="text-xs text-zinc-500">
                Total records: <strong className="text-white">{diagnoseReport.total}</strong>
              </div>

              {(diagnoseReport.invalid_id_count + diagnoseReport.ghost_user_count) > 0 ? (
                <div className="p-3 bg-rose-500/5 border border-rose-500/20 rounded space-y-3">
                  <p className="text-xs text-rose-300">
                    Found <strong>{diagnoseReport.invalid_id_count + diagnoseReport.ghost_user_count}</strong> broken record(s).
                    Deleting them is safe — they no longer belong to any active user.
                  </p>
                  <Input
                    value={purgeConfirmText}
                    onChange={(e) => setPurgeConfirmText(e.target.value)}
                    placeholder="Type PURGE to confirm"
                    className="bg-black/40 border-rose-500/30 font-mono text-sm"
                    disabled={diagnoseBusy}
                    data-testid="purge-confirm-input"
                  />
                  <Button
                    onClick={runPurge}
                    disabled={diagnoseBusy || purgeConfirmText !== 'PURGE'}
                    className="w-full bg-rose-600 hover:bg-rose-700"
                    data-testid="confirm-purge-btn"
                  >
                    {diagnoseBusy ? 'Purging…' : 'Purge broken records'}
                  </Button>
                </div>
              ) : (
                <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded text-xs text-emerald-300 text-center">
                  All attendance records are healthy — nothing to repair.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanelPage;
